import type { PushArt, PushNachricht } from '../../shared/api.js';
import { api, fehlerText, modus } from '../api.js';
import { abschnitt, ersetze, h, haengeAn, knopf, liste, meldung, schritte, setzeMeldung, zeile } from '../dom.js';
import { melde } from '../ergebnisse.js';
import { istAndroid, istIos } from '../geraet.js';
import type { Ansicht } from '../router.js';
import { holeStand } from '../sitzung.js';
import { lies } from '../speicher.js';
import { formatiereUhrzeit, formatiereZeitpunkt } from '../zeit.js';
import { Aufraeumer, kopf, pruefpunktStand } from './gemeinsam.js';

function schluesselAlsBytes(base64url: string): Uint8Array<ArrayBuffer> {
  const base64 = (base64url + '='.repeat((4 - (base64url.length % 4)) % 4)).replace(/-/g, '+').replace(/_/g, '/');
  const roh = atob(base64);
  const bytes = new Uint8Array(new ArrayBuffer(roh.length));
  for (let i = 0; i < roh.length; i++) bytes[i] = roh.charCodeAt(i);
  return bytes;
}

export function pushMoeglich(): boolean {
  return 'serviceWorker' in navigator && 'PushManager' in window && 'Notification' in window;
}

async function sendeAbo(abo: PushSubscription): Promise<void> {
  const json = abo.toJSON();
  await api('/api/push/abo', { methode: 'PUT', json: { endpoint: json.endpoint, keys: json.keys } });
}

/** Beim Start: bestehendes Abo erneut melden, damit der Server Änderungen erfährt (Konzept, Push-Abos sauber führen). */
export async function pruefePushAbo(): Promise<void> {
  if (!pushMoeglich() || Notification.permission !== 'granted') return;
  // Ohne laufenden Service Worker nicht ewig warten, sonst hängen die übrigen Startaufgaben.
  const registrierung = await Promise.race([
    navigator.serviceWorker.ready,
    new Promise<undefined>((fertig) => window.setTimeout(() => fertig(undefined), 5_000)),
  ]);
  if (!registrierung) return;
  const abo = await registrierung.pushManager.getSubscription();
  if (abo) await sendeAbo(abo);
}

const ART_TEXT: Record<PushArt, string> = { sofort: 'sofort', zeitversetzt: 'zeitversetzt', neustart: 'Neustart-Test', manuell: 'von der Auswertung' };

function hinweisAbgelehnt(): string {
  if (istIos()) return 'Wieder einschalten: Einstellungen → Mitteilungen → TFM Test → Mitteilungen erlauben.';
  if (istAndroid()) return 'Wieder einschalten: lange auf das App-Symbol tippen → App-Info → Benachrichtigungen.';
  return 'Wieder einschalten: in den Website-Einstellungen des Browsers Benachrichtigungen erlauben.';
}

export const push: Ansicht = async (ziel, _params, suche) => {
  const aufraeumer = new Aufraeumer();
  const status = meldung();
  const neustartStatus = meldung();
  const zustand = h('div', {});
  const nachrichtenListe = h('div', {});
  const aktivieren = h('div', {});
  const geoeffnet = suche.get('nachricht');

  async function zeigeZustand(): Promise<void> {
    const erlaubnis = 'Notification' in window ? Notification.permission : 'nicht vorhanden';
    let abo = false;
    if (pushMoeglich()) abo = (await (await navigator.serviceWorker.ready).pushManager.getSubscription()) !== null;
    const text: Record<string, string> = { granted: 'erlaubt', denied: 'abgelehnt', default: 'noch nicht gefragt' };
    ersetze(zustand, liste(zeile('Benachrichtigungen', text[erlaubnis] ?? erlaubnis), zeile('Push-Abo auf diesem Gerät', abo ? 'ja' : 'nein'), zeile('Läuft als', modus() === 'app' ? 'installierte App' : 'Browser')));
    ersetze(
      aktivieren,
      erlaubnis === 'denied'
        ? h('p', { class: 'meldung meldung-fehler' }, `Benachrichtigungen sind abgelehnt. ${hinweisAbgelehnt()}`)
        : !abo && knopf('Benachrichtigungen aktivieren', () => aktiviere()),
    );
  }

  async function aktiviere(): Promise<void> {
    const vapid = await lies('vapidPublicKey');
    if (!vapid) {
      setzeMeldung(status, 'Schlüssel fehlt: einmal mit Netz öffnen.', 'fehler');
      return;
    }
    // Die Frage darf erst nach einem Tippen kommen (iPhone).
    const erlaubnis = await Notification.requestPermission();
    if (erlaubnis !== 'granted') {
      await melde('push', 'fehler', `Benachrichtigungen nicht erlaubt (${erlaubnis}).`, { erlaubnis });
      setzeMeldung(status, hinweisAbgelehnt(), 'fehler');
      await zeigeZustand();
      return;
    }
    try {
      const registrierung = await navigator.serviceWorker.ready;
      const abo = await registrierung.pushManager.subscribe({ userVisibleOnly: true, applicationServerKey: schluesselAlsBytes(vapid) });
      await sendeAbo(abo);
      setzeMeldung(status, 'Push ist eingeschaltet. Jetzt einen Test-Push senden.', 'ok');
    } catch (fehler) {
      await melde('push', 'fehler', `Push-Abo fehlgeschlagen: ${fehlerText(fehler)}`);
      setzeMeldung(status, fehlerText(fehler), 'fehler');
    }
    await zeigeZustand();
  }

  async function sende(verzoegerungSek: number, art: PushArt, ausgabe: HTMLElement): Promise<void> {
    try {
      const n = await api<PushNachricht>('/api/push/senden', { json: { verzoegerungSek, art } });
      setzeMeldung(
        ausgabe,
        verzoegerungSek === 0
          ? `Gesendet um ${formatiereUhrzeit(n.gesendetAm ?? n.faelligAm)} (${n.sendeStatus ?? 'unbekannt'}). Die Nachricht sollte gleich erscheinen.`
          : `Kommt um ${formatiereUhrzeit(n.faelligAm)} Uhr.`,
        'ok',
      );
      await zeigeNachrichten();
    } catch (fehler) {
      setzeMeldung(ausgabe, fehlerText(fehler), 'fehler');
    }
  }

  async function zeigeNachrichten(): Promise<void> {
    try {
      const liste = await api<PushNachricht[]>('/api/push/nachrichten');
      ersetze(
        nachrichtenListe,
        liste.length === 0
          ? h('p', { class: 'leise' }, 'Noch keine Nachrichten.')
          : h(
              'ul',
              { class: 'nachrichten' },
              ...liste.map((n) =>
                h(
                  'li',
                  { class: n.id === geoeffnet ? 'hervorgehoben' : '' },
                  h('strong', {}, `${ART_TEXT[n.art]}, fällig ${formatiereZeitpunkt(n.faelligAm)}`),
                  h('span', {}, n.gesendetAm ? `gesendet ${formatiereUhrzeit(n.gesendetAm)}: ${n.sendeStatus ?? ''}` : 'wartet auf den Versand'),
                  h('span', {}, n.empfangenAm ? [h('span', { 'aria-hidden': 'true' }, '✓ '), `angekommen ${formatiereUhrzeit(n.empfangenAm)}`] : 'noch nicht bestätigt'),
                ),
              ),
            ),
      );
      if (liste.some((n) => n.empfangenAm)) await holeStand().catch(() => {});
    } catch (fehler) {
      ersetze(nachrichtenListe, h('p', { class: 'meldung meldung-fehler' }, fehlerText(fehler)));
    }
  }

  const verzoegerung = h(
    'select',
    { id: 'neustart-verzoegerung', name: 'neustart-verzoegerung' },
    ...[3, 5, 10].map((min) => h('option', { value: min * 60, selected: min === 5 }, `${min}\u00a0Minuten`)),
  );

  if (!pushMoeglich()) {
    const nurApp = istIos() && modus() === 'browser';
    if (!nurApp) await melde('push', 'nicht_unterstuetzt', 'Der Browser kann kein Web Push.');
    haengeAn(ziel, [
      ...kopf('Push'),
      h('p', { class: 'meldung meldung-fehler' }, nurApp ? 'Am iPhone gibt es Push nur in der installierten App.' : 'Dieser Browser kann kein Web Push.'),
    ]);
    return aufraeumer.alles;
  }

  haengeAn(ziel, [
    ...kopf('Push', 'Kommt eine Push-Nachricht an, auch nach einem Neustart des Handys? Der Service Worker bestätigt den Empfang selbst an den Server.'),
    geoeffnet && h('p', { class: 'meldung meldung-ok' }, 'Geöffnet über eine Push-Nachricht.'),
    abschnitt('Einschalten', zustand, aktivieren, status),
    abschnitt(
      'Test-Push',
      pruefpunktStand('push', aufraeumer),
      h('div', { class: 'knopfreihe' }, knopf('Test-Push jetzt senden', () => sende(0, 'sofort', status)), knopf('In 3 Minuten senden', () => sende(180, 'zeitversetzt', status), 'sekundaer')),
    ),
    abschnitt(
      'Push nach Neustart',
      pruefpunktStand('push-neustart', aufraeumer),
      schritte(
        'Wartezeit wählen und den Neustart-Test starten.',
        'Handy ausschalten und wieder einschalten, einmal entsperren, dann sperren.',
        'App nicht öffnen. Zur angegebenen Zeit muss die Nachricht auf dem Sperrbildschirm erscheinen.',
        'Auf die Nachricht tippen: Die App zeigt den Empfang.',
      ),
      h('div', { class: 'feld' }, h('label', { for: 'neustart-verzoegerung' }, 'Wartezeit'), verzoegerung),
      knopf('Neustart-Test starten', () => sende(Number(verzoegerung.value), 'neustart', neustartStatus)),
      neustartStatus,
    ),
    abschnitt('Letzte Nachrichten', nachrichtenListe, knopf('Aktualisieren', () => zeigeNachrichten(), 'sekundaer')),
  ]);
  await zeigeZustand();
  await zeigeNachrichten();
  aufraeumer.intervall(() => void zeigeNachrichten(), 15_000);
  return aufraeumer.alles;
};
